import { clearState } from "core/store/enhancers/clearStateEnhance";
import { IRequestAuth } from "core/api";
import { historyPushRequest } from "../history";
import {
  getReviewers,
  getReviewer,
  IGetReviewersResponse,
  IGetReviewerResponse,
  ICreateTechniciansResponse,
  IGetTechniciansResponse,
  IRemoveTechnicianResponse,
  resetPassword,
  createTechnicians,
  getTechnicians,
  getTechnicianPhysicians,
  getRegistrationCodeValidity,
  removeTechnician,
} from "core/api/study_reviewer";
import { startSubmit, stopSubmit, reset } from "redux-form";
import { delay } from "redux-saga";
import {
  all,
  call,
  fork,
  put,
  race,
  select,
  take,
  takeLatest,
} from "redux-saga/effects";
import * as Features from "study_reviewer/features";
import * as routes from "../../routes";
import { actions as mainActions } from "../main";
import {
  clearReviewers,
  getReviewersRequest as getReviewersRequestAction,
  IActionDeactivateReviewerRequest,
  IActionGetReviewersRequest,
  IActionGetReviewerRequest,
  IActionGetTechnicianPhysiciansRequest,
  resetIsFetchingReviewers,
  resetIsReviewerActivationConfirmActive,
  setIsFetchingReviewers,
  setReviewers,
  setCurrentReviewer,
  IActionPasswordResetRequest,
} from "./actions";
import * as actions from "./actionTypes";
import { ISelectReviewer } from "./model";
import { selectReviewer } from "./selectors";

/**
 * Dependencies needed for the sagas
 */
export interface IDependencies extends IMainSagaDependencies {}

/**
 * Dependencies that need to be injected into the main saga
 */
export interface IMainSagaDependencies {
  withAuth: (fn: (...args: any[]) => any) => void;
  withAuthNoRefresh: (fn: (...args: any[]) => any) => void;
  withTokenAuth: (fn: (auth: IRequestAuth, ...args: any[]) => any) => void;
  onTokenExpired: () => void;
  redirectTo: (path: string) => void;
}

const GET_REVIEWERS_REQUEST_COMPLETE = {
  type: "reviewers/GET_REVIEWERS_REQUEST_COMPLETE",
};

const GET_REVIEWER_REQUEST_COMPLETE = {
  type: "reviewers/GET_REVIEWER_REQUEST_COMPLETE",
};
/**
 * Saga for calling `getReviewersRequest` and waiting on completion (or timeout).
 *
 * Usage: `call(getReviewersRequestWait)`
 */
export function* getReviewersRequestWait() {
  yield put(getReviewersRequestAction());
  yield race({
    isComplete: take(GET_REVIEWERS_REQUEST_COMPLETE.type),
    isTimeout: call(delay, 30000), // timeout 30 seconds
  });
}

export function* getReviewersRequest(
  dependencies: IDependencies,
  action: IActionGetReviewersRequest
) {
  yield put(setIsFetchingReviewers());
  let response: IGetReviewersResponse;
  try {
    response = yield call(dependencies.withAuth, getReviewers);
    if (response.status === 200) {
      yield put(clearReviewers());
      const reviewers = Object.keys(response.data.studyReviewers).map(
        (reviewerId) => {
          const reviewer = response.data.studyReviewers[reviewerId];
          return {
            id: reviewerId,
            firstName: reviewer.firstName,
            lastName: reviewer.lastName,
            email: reviewer.email,
            npi: reviewer.npi,
            isApproved: reviewer.isApproved,
            approvedTime: reviewer.approvedTime,
            isAdmin: reviewer.isAdmin,
            createdTime: reviewer.createdTime,
            isTechnician: reviewer.isTechnician,
            technicianNPIs: reviewer.technicianNPIs,
          };
        }
      );
      yield put(setReviewers(reviewers));
    } else if (response.status === 401) {
      yield call(dependencies.onTokenExpired);
    } else {
      // TODO I18N
      yield put(
        mainActions.snackbarSetRequest(
          "Oops! Something went wrong. Please try again later."
        )
      );
    }
  } catch (e) {
    // TODO I18N
    yield put(
      mainActions.snackbarSetRequest(
        "Network error, please check your connection and try again."
      )
    );
    yield call(dependencies.redirectTo, routes.rootRoute());
    yield put(resetIsFetchingReviewers());
    yield put(GET_REVIEWERS_REQUEST_COMPLETE);
    return;
  }
  yield put(resetIsFetchingReviewers());
  yield put(GET_REVIEWERS_REQUEST_COMPLETE);
}

export function* getReviewerRequest(
  dependencies: IDependencies,
  action: IActionGetReviewerRequest
) {
  yield put(setIsFetchingReviewers());
  let response: IGetReviewerResponse;
  try {
    response = yield call(dependencies.withAuth, getReviewer);
    if (response.status === 200) {
      const reviewer = response.data.studyReviewer;
      yield put(
        setCurrentReviewer({
          id: reviewer.id,
          firstName: reviewer.firstName,
          lastName: reviewer.lastName,
          email: reviewer.email,
          npi: reviewer.npi,
          isApproved: reviewer.isApproved,
          approvedTime: reviewer.approvedTime,
          isAdmin: reviewer.isAdmin,
          createdTime: reviewer.createdTime,
          isTechnician: reviewer.isTechnician,
          technicianNPIs: reviewer.technicianNPIs,
        })
      );
    } else if (response.status === 401) {
      yield call(dependencies.onTokenExpired);
    } else {
      // TODO I18N
      yield put(
        mainActions.snackbarSetRequest(
          "Oops! Something went wrong. Please try again later."
        )
      );
    }
  } catch (e) {
    // TODO I18N
    yield put(
      mainActions.snackbarSetRequest(
        "Network error, please check your connection and try again."
      )
    );
    yield put(resetIsFetchingReviewers());
    yield put(GET_REVIEWER_REQUEST_COMPLETE);
    return;
  }
  yield put(resetIsFetchingReviewers());
  yield put(GET_REVIEWER_REQUEST_COMPLETE);
}

export function* passwordResetRequest(action: IActionPasswordResetRequest) {
  yield put(startSubmit(action.form));
  try {
    const response = yield call(resetPassword, action.payload);
    if (response.status === 200) {
      yield put(stopSubmit(action.form));
      yield put(historyPushRequest("/login"));
      yield put(
        mainActions.snackbarSetRequest(
          `Request received successfully. If an account exists a reset link will be sent.`
        )
      );
    }
    if (response.status === 409) {
      const error = response.data.message;
      yield put(stopSubmit(action.form, { _error: error }));
    }
  } catch (e) {
    if (!e.response) {
      // TODO I18N
      const error =
        "Network error. Please check your connection and try again.";
      yield put(stopSubmit(action.form, { _error: error }));
    } else {
      // TODO I18N
      let error = "Server-side error";
      if (e.response && e.response.status === 409) {
        // TODO I18N
        error = "Duplicate Error";
      }
      yield put(stopSubmit(action.form, { _error: error }));
    }
  }
}

export function* getTechnicianPhysiciansRequest(
  dependencies: IDependencies,
  action: IActionGetTechnicianPhysiciansRequest
) {
  yield put(setIsFetchingReviewers());
  try {
    const response = yield call(
      dependencies.withAuthNoRefresh,
      getTechnicianPhysicians,
      action.payload
    );
    if (response.status === 200) {
      yield put(clearReviewers());
      const { physicians } = response.data;
      yield put(setReviewers(physicians));
    } else if (response.status === 401) {
      yield call(dependencies.onTokenExpired);
    } else {
      // TODO I18N
      yield put(
        mainActions.snackbarSetRequest(
          "Oops! Something went wrong. Please try again later."
        )
      );
    }
  } catch (e) {
    // TODO I18N
    yield put(
      mainActions.snackbarSetRequest(
        "Network error, please check your connection and try again."
      )
    );
    yield put(resetIsFetchingReviewers());
    yield put(GET_REVIEWER_REQUEST_COMPLETE);
    return;
  }
  yield put(resetIsFetchingReviewers());
  yield put(GET_REVIEWER_REQUEST_COMPLETE);
}

function* mainSaga(dependencies: IMainSagaDependencies) {
  const sagaDependencies = {
    ...dependencies,
  };
  yield all([
    takeLatest<IActionGetReviewersRequest, IDependencies>(
      actions.GET_REVIEWERS_REQUEST,
      getReviewersRequest,
      sagaDependencies
    ),
    takeLatest<IActionGetReviewerRequest, IDependencies>(
      actions.GET_REVIEWER_REQUEST,
      getReviewerRequest,
      sagaDependencies
    ),
    takeLatest<IActionGetTechnicianPhysiciansRequest, IDependencies>(
      actions.GET_TECHNICIAN_PHYSICIANS_REQUEST,
      getTechnicianPhysiciansRequest,
      sagaDependencies
    ),
    takeLatest(actions.PASSWORD_FORM_NAME, passwordResetRequest),
  ]);
}

function* saga(dependencies: IMainSagaDependencies) {
  yield all([fork<IMainSagaDependencies>(mainSaga, dependencies)]);
}

export default saga;



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_reviewer/src/store/reviewers/saga.ts